import type { IssueType } from '@servicetim/shared';
import { useDeleteIssueType } from '@servicetim/ui/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ProblemBanner,
  toast,
} from '@servicetim/ui/components';
import { useEffect, useId, useState } from 'react';
import { fieldErrorsOf, messageAt, problemDetail } from '../lib/field-errors';
import { FormRow } from '../ui/form-row';
import { SimpleSelect } from '../ui/simple-select';

export interface DeleteIssueTypeDialogProps {
  projectId: string;
  issueType: IssueType | null;
  others: readonly IssueType[];
  onOpenChange: (open: boolean) => void;
}

/**
 * Delete an issue type. Once tickets use it the API answers 409 `issue_type.in_use`, so the
 * dialog offers a "move tickets to" target (`?moveTo=`), which runs an `issue_type_move` job.
 */
export function DeleteIssueTypeDialog({
  projectId,
  issueType,
  others,
  onOpenChange,
}: DeleteIssueTypeDialogProps) {
  const id = useId();
  const remove = useDeleteIssueType(projectId);
  const [moveTo, setMoveTo] = useState<string | undefined>(undefined);
  const open = issueType !== null;
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset when a new row is chosen
  useEffect(() => {
    if (open) {
      setMoveTo(undefined);
      remove.reset();
    }
  }, [open, issueType?.id]);

  const detail = problemDetail(remove.error);
  const inUse = detail === 'issue_type.in_use';
  const moveError = messageAt(fieldErrorsOf(remove.error), 'moveToIssueTypeId');

  const submit = async () => {
    if (!issueType) return;
    try {
      await remove.mutateAsync({ id: issueType.id, moveToIssueTypeId: moveTo });
      toast.success(`Issue type "${issueType.name}" deleted`);
      onOpenChange(false);
    } catch {
      // banner / inline
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Delete {issueType?.name}?</DialogTitle>
          <DialogDescription>
            Forms dedicated to this type are deleted and field bindings stop mentioning it. Tickets
            of this type must move to another type.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          {remove.error && !inUse ? <ProblemBanner error={remove.error} size="sm" /> : null}
          <FormRow
            label="Move existing tickets to"
            htmlFor={`${id}-move`}
            hint="Optional until tickets exist; required once they do."
            error={
              inUse ? (moveError ?? 'Tickets use this type; choose where they go.') : undefined
            }
          >
            <SimpleSelect
              id={`${id}-move`}
              value={moveTo}
              onChange={setMoveTo}
              options={others.map((t) => ({ value: t.id, label: t.name }))}
              emptyLabel="Do not move (no tickets)"
              invalid={inUse}
            />
          </FormRow>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="destructive" disabled={remove.isPending}>
              Delete
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
